/**
 * Section-level drop target that wraps a whole photo category.
 *
 * When a user drops a batch of image files onto the section, we
 * auto-route them across the section's empty requirement slots —
 * one image per slot in order — and spill the overflow onto the
 * first non-N/A slot as extras. This is the fast path for a desktop
 * user who just finished a site visit and wants to drag 15 photos
 * from a Downloads folder onto "Pre-Con Photos" without clicking
 * every individual slot's camera button.
 *
 * Per-card drops still work and take precedence — PhotoReqCard's
 * `onDrop` calls `e.stopPropagation()` so we never double-process.
 *
 * All drop logic lives in the parent (PhotosPanel.handleSectionDrop)
 * so this file stays presentational.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { partitionImages, isFileOrPhotoDrag, isInternalPhotoDrag } from "@/lib/photoDrop";

export default function SectionDropzone({
  categoryName,
  enabled = true,
  slotsAvailable = 0,
  allowVideo = false,
  onDropFiles,
  children,
}) {
  const [dragOver, setDragOver] = useState(false);
  // Same counter trick as PhotoReqCard — children fire enter/leave
  // during pointer traversal and would flicker a plain boolean.
  const dragDepth = useRef(0);

  // A card inside us stops drop propagation and browsers skip dragleave after a drop,
  // so clear the highlight on any drop/dragend anywhere on the page.
  useEffect(() => {
    const reset = () => { dragDepth.current = 0; setDragOver(false); };
    window.addEventListener("drop", reset, true);
    window.addEventListener("dragend", reset, true);
    return () => {
      window.removeEventListener("drop", reset, true);
      window.removeEventListener("dragend", reset, true);
    };
  }, []);

  const onDragEnter = (e) => {
    if (!enabled) return;
    if (!isFileOrPhotoDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!enabled) return;
    if (!isFileOrPhotoDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = isInternalPhotoDrag(e) ? "move" : "copy";
  };
  const onDragLeave = () => {
    if (!enabled) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    if (!enabled) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    // Moving an existing photo needs a specific slot — never upload the dragged thumbnail.
    if (isInternalPhotoDrag(e)) {
      toast.info("Drop the photo onto a specific photo slot to move it.");
      return;
    }
    const all = Array.from(e.dataTransfer?.files || []);
    const { images, skipped } = partitionImages(all, { allowVideo });
    if (all.length && !images.length) {
      toast.error(allowVideo ? "Only photos or videos can be dropped on this section." : "Only image files can be dropped on a photo section.");
      return;
    }
    if (skipped.length > 0) {
      toast.info(`Skipped ${skipped.length} non-image file${skipped.length === 1 ? "" : "s"}.`);
    }
    if (images.length) {
      onDropFiles?.(images);
    }
  };

  // The hint text adapts: if every slot is already filled, we tell
  // the user the batch will land on the first slot as extras so the
  // affordance isn't a lie.
  const hint = slotsAvailable > 0
    ? `Drop to fill ${slotsAvailable} open slot${slotsAvailable === 1 ? "" : "s"} in "${categoryName}"`
    : `Drop to add extras to "${categoryName}"`;

  return (
    <div
      className="relative"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={`section-dropzone-${categoryName.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {children}
      {dragOver && (
        <div
          className="absolute inset-0 rounded-lg bg-blue-950/40 border-2 border-dashed border-blue-400 flex items-center justify-center pointer-events-none z-20"
          data-testid={`section-drop-overlay-${categoryName.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <div className="text-blue-100 font-display font-bold text-sm tracking-wider uppercase bg-blue-900/85 px-4 py-2 rounded shadow">
            {hint}
          </div>
        </div>
      )}
    </div>
  );
}
